// The commissioner log on the wire. screens/AUDIT_LOG.md, scenario J5,
// BACKLOG E9's audit feed.
//
// THE SENTENCE IS THE SERVER'S. A second client shows the same words for the
// same act, so the words are written where the act is read, not in a screen.
// The refs are what a client needs to open the act's own page, and every one
// is null when the act has none.

import { z } from 'zod';
import { pageOf } from './envelope';

/**
 * Every kind of act the log records today. A stored kind this build does not
 * know is still served, as `other`, with a plain sentence. `score.adjust` is a
 * stat correction's re-score, the system's; `points.adjust` is a
 * commissioner's. `waivers.priority` and `waivers.budget` are J7's: a team
 * moved in the waiver order, a team's budget set. `seat.invite` is J4's: a
 * team handed to a new manager through the commissioner's link, written when
 * the link is used. `seat.bot` and `seat.unbot` are J3's: a bot put on a team
 * nobody runs, and taken off it.
 */
export const AuditKind = z.enum([
  'settings.change',
  'trade.approve',
  'trade.veto',
  'seat.remove',
  'seat.close',
  'seat.invite',
  'seat.bot',
  'seat.unbot',
  'invite.rotate',
  'commissioner.handover',
  'division.set',
  // A rival named for a team, or none, and the schedule drawn again, before
  // the season opens (PLAN-2026-10-29-the-schedule-before-the-season).
  'rival.set',
  'schedule.draw',
  'lineup.fix',
  'points.adjust',
  'score.adjust',
  'transaction.reverse',
  'waivers.priority',
  'waivers.budget',
  'season.complete',
  // A commissioner started the next season from this one (H6), in the new
  // season's log.
  'season.next',
  'league.eliminated',
  // A message in the league's chat hidden by the commissioner, and shown again (I9).
  'chat.hide',
  'chat.show',
  'other',
]);
export type AuditKind = z.infer<typeof AuditKind>;

export const AuditEntry = z.object({
  id: z.string(),
  at: z.string(),
  kind: AuditKind,
  /** A person by their name as it was then, or the system ("Stat correction", "The league"). */
  actor: z.object({ kind: z.enum(['person', 'system']), name: z.string() }),
  /** One plain sentence with no scores in it, the same at every detail level. */
  sentence: z.string(),
  /** The numbers behind it, for Standard and up: a correction's scores. Null when there are none. */
  detail: z.string().nullable(),
  /** Why, in a person's own words: a rule change's or a veto's reason. Null for the system's acts. */
  reason: z.string().nullable(),
  refs: z.object({
    /** The rules version a change made. */
    version: z.number().int().nullable(),
    period: z.number().int().nullable(),
    matchupId: z.string().nullable(),
    tradeId: z.string().nullable(),
    teamId: z.string().nullable(),
  }),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

/** `GET /api/leagues/:id/audit` as a query string. */
export const AuditQuery = z.object({
  cursor: z.string().nullable().default(null),
  limit: z.number().int().min(1).max(100).default(50),
});
export type AuditQuery = z.infer<typeof AuditQuery>;

/** A page of the log, newest first; `nextCursor` walks back in time and belongs to its league. */
export const AuditPage = pageOf(AuditEntry);
export type AuditPage = z.infer<typeof AuditPage>;
